import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  TrendingUp, Users, DollarSign, BarChart3, Zap, 
  CheckCircle, ArrowRight, Lock, X
} from 'lucide-react';

const LOGO_HORIZ_WHITE = 'https://customer-assets.emergentagent.com/job_canepas-platform/artifacts/qmhx033y_Logo_Artificialmente_H_Blanco.png';
const PASS = '123321';

export default function NuevaPropuesta() {
  const navigate = useNavigate();
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState(false);

  const handleAccessDemo = () => {
    setPasswordModalOpen(true);
  };

  const handleSubmitPassword = (e) => {
    e.preventDefault();
    if (password === PASS) {
      localStorage.setItem('demo_authenticated', '1');
      navigate('/propuesta/demo/dashboard');
    } else {
      setError(true);
      setTimeout(() => setError(false), 2500);
    }
  };

  const funcionalidades = [
    {
      icon: <DollarSign size={24} />,
      title: 'Autodata API',
      description: 'Precio de toma y venta oficial desde la base de datos de Autodata.com.uy'
    },
    {
      icon: <TrendingUp size={24} />,
      title: 'Scraping MercadoLibre',
      description: 'Promedio de mercado + publicaciones menor y mayor con links directos'
    },
    {
      icon: <Users size={24} />,
      title: 'Tasación de Gerencia',
      description: 'Valoración de los dos dueños vía WhatsApp integrada al sistema'
    },
    {
      icon: <BarChart3 size={24} />,
      title: 'Sistema de Ofertas',
      description: 'Revendedores registrados ofertan en tiempo real. Ofertas dinámicas ordenadas de mayor a menor'
    },
    {
      icon: <CheckCircle size={24} />,
      title: 'Expectativa Cliente',
      description: 'Registro de la expectativa del dueño del vehículo desde la carga inicial'
    },
    {
      icon: <Zap size={24} />,
      title: 'Adjudicación Directa',
      description: 'Opción de adjudicar el vehículo a un revendedor sin pasar por reventa tradicional'
    }
  ];

  const pricingOptions = [
    {
      name: 'Desarrollo e Implementación',
      price: 'USD 4,250',
      subtitle: 'Pago único',
      features: [
        'Sistema de tasación multi-fuente completo',
        'Integración Autodata API',
        'Scraping automatizado MercadoLibre',
        'Sistema de ofertas dinámico de revendedores',
        'Panel de gestión administrativo',
        'Carga de vehículos con OCR de libreta',
        'Notificaciones automatizadas WhatsApp',
        'Dashboard con métricas y KPIs',
        'Versión mobile-first 100% responsive',
        'Capacitación del equipo incluida',
        'Soporte durante implementación (3 meses)'
      ],
      highlighted: true
    },
  ];

  const monthlyOptions = [
    {
      tag: 'RECOMENDADO',
      name: 'Porcentaje / Comisión',
      subtitle: 'Sin cuota mensual · Modelo compartido',
      price: '$0',
      priceNote: 'por rendimiento',
      dotted: true,
      features: [
        'Soporte técnico',
        'Atención 24/7',
        'Actualizaciones periódicas',
        'Resolución de incidencias',
        'Ajustes y mejoras continuas',
        'Gastos de Meta (plantillas, consumo, etc.)',
        'Costos de tokens de OpenAI API incluidos',
        'Google Cloud hosting incluido',
        'Monitoreo constante',
        'Escalabilidad integrada',
        'Modelo de rendimiento compartido',
      ],
      note: 'Para definir esta modalidad se requiere un análisis previo del flujo estimado de ventas y operaciones del grupo, con el fin de establecer un porcentaje justo y sostenible para ambas partes.',
    },
    {
      tag: null,
      name: 'Mantenimiento Fijo',
      subtitle: 'Cuota fija mensual · Funcionamiento cubierto por el cliente',
      price: 'USD 430',
      priceNote: 'por mes',
      dotted: false,
      features: [
        'Soporte técnico',
        'Resolución de incidencias',
        'Actualizaciones',
        'Atención (días hábiles)',
        'Monitoreo periódico',
        'Ajustes puntuales según necesidades',
      ],
      excluded: [
        'Gastos de servicios externos (Meta, OpenAI API, hosting, etc.)',
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      {/* Header/Nav */}
      <nav className="border-b border-gray-800 bg-gray-900/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <img src={LOGO_HORIZ_WHITE} alt="Artificialmente" className="h-8 w-auto" />
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/')}
              className="text-sm text-gray-400 hover:text-white transition"
            >
              ← Propuesta Original
            </button>
            <button
              onClick={handleAccessDemo}
              className="px-5 py-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white text-sm font-medium rounded-lg hover:from-orange-600 hover:to-orange-700 transition"
            >
              Acceder a la Demo
            </button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="px-6 py-20 sm:py-32">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500/10 border border-orange-500/20 rounded-full text-orange-400 text-sm font-medium mb-8">
            <span>🚗</span>
            <span>Sistema de Tasación Integrado</span>
          </div>
          
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Decisiones basadas en<br /><span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-orange-600">datos sólidos y reales.</span>
          </h1>
          
          <p className="text-lg text-gray-400 max-w-3xl mx-auto mb-12 leading-relaxed">
            Sistema que centraliza tasaciones de múltiples fuentes (Autodata, MercadoLibre, gerencia, vendedores y revendedores) en un solo lugar. Desde la carga del vehículo hasta la decisión de compra o adjudicación.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={handleAccessDemo}
              className="w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white font-semibold rounded-lg hover:from-orange-600 hover:to-orange-700 transition shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
            >
              <Lock size={18} />
              <span>Ver Demo Interactiva</span>
              <ArrowRight size={18} />
            </button>
            <a
              href="#funcionalidades"
              className="w-full sm:w-auto px-8 py-4 border border-gray-700 text-gray-300 font-semibold rounded-lg hover:bg-gray-800 transition"
            >
              Conocer más
            </a>
          </div>
        </div>
      </section>

      {/* Cómo Funciona - Proceso Detallado */}
      <section className="px-6 py-20 border-b border-gray-800">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              ¿Cómo funciona el sistema?
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Proceso completo desde que ingresa el vehículo hasta la decisión final de compra o adjudicación
            </p>
          </div>

          <div className="space-y-8">
            {/* Paso 1 - PRINCIPAL (GRANDE) */}
            <div className="relative">
              <div className="absolute -left-6 top-0 w-12 h-12 rounded-full bg-gray-700 border-2 border-orange-500/60 flex items-center justify-center text-white font-bold text-lg shadow-lg z-10">
                1
              </div>
              <div className="bg-gray-900 border border-orange-500/25 rounded-2xl p-8 ml-8 shadow-2xl">
                <h3 className="text-2xl font-bold mb-4 text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Carga del Vehículo en el Formulario
                </h3>
                <p className="mb-6 text-gray-300 leading-relaxed">
                  El vendedor ingresa la publicación desde su celular. Este es el punto de partida que dispara todo el flujo automatizado.
                </p>

                <div className="grid md:grid-cols-2 gap-5 mb-6">
                  <div className="bg-gray-800 rounded-xl p-5 border border-gray-700">
                    <h4 className="font-bold mb-3 text-base text-white">Opción A: Cargar Libreta</h4>
                    <ul className="space-y-2 text-sm text-gray-400">
                      <li className="flex items-start gap-2"><span className="text-orange-400 mt-0.5 flex-shrink-0">→</span><span>Sacar foto a la libreta de conducir</span></li>
                      <li className="flex items-start gap-2"><span className="text-orange-400 mt-0.5 flex-shrink-0">→</span><span>El sistema extrae automáticamente: marca, modelo, año, matrícula, motor</span></li>
                      <li className="flex items-start gap-2"><span className="text-orange-400 mt-0.5 flex-shrink-0">→</span><span>El vendedor completa datos faltantes</span></li>
                    </ul>
                  </div>
                  <div className="bg-gray-800 rounded-xl p-5 border border-gray-700">
                    <h4 className="font-bold mb-3 text-base text-white">Opción B: Carga Manual</h4>
                    <ul className="space-y-2 text-sm text-gray-400">
                      <li className="flex items-start gap-2"><span className="text-orange-400 mt-0.5 flex-shrink-0">→</span><span>Ingresar todos los datos manualmente</span></li>
                      <li className="flex items-start gap-2"><span className="text-orange-400 mt-0.5 flex-shrink-0">→</span><span>Marca, modelo, año, kilometraje, motor, etc.</span></li>
                    </ul>
                  </div>
                </div>

                <div className="bg-gray-800 rounded-xl p-5 border border-gray-700 mb-5">
                  <h4 className="font-bold mb-3 text-base text-white">Datos Adicionales</h4>
                  <ul className="grid md:grid-cols-2 gap-x-6 gap-y-2 text-sm text-gray-400">
                    <li className="flex items-start gap-2"><span className="text-orange-400 mt-0.5">✓</span><span>Subir mínimo 10 fotos del vehículo</span></li>
                    <li className="flex items-start gap-2"><span className="text-orange-400 mt-0.5">✓</span><span>Ingresar expectativa del cliente</span></li>
                    <li className="flex items-start gap-2"><span className="text-orange-400 mt-0.5">✓</span><span>Ingresar valoración del vendedor</span></li>
                    <li className="flex items-start gap-2"><span className="text-orange-400 mt-0.5">✓</span><span>Observaciones y descripción</span></li>
                  </ul>
                </div>

                <div className="p-5 bg-gray-800 rounded-xl border border-orange-500/30">
                  <div className="font-semibold mb-2 text-white text-sm">Al publicar, el sistema envía automáticamente a:</div>
                  <div className="grid md:grid-cols-3 gap-3 mt-3">
                    <div className="flex items-center gap-2 text-sm text-gray-300 bg-gray-900/60 rounded-lg px-3 py-2">
                      <span className="text-orange-400 flex-shrink-0">→</span><span>Todos los revendedores registrados</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-300 bg-gray-900/60 rounded-lg px-3 py-2">
                      <span className="text-orange-400 flex-shrink-0">→</span><span>Los 2 dueños (gerencia) vía WhatsApp</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-300 bg-gray-900/60 rounded-lg px-3 py-2">
                      <span className="text-orange-400 flex-shrink-0">→</span><span>Sistemas automatizados (Autodata, ML)</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Pasos secundarios (más pequeños) */}
            <div className="grid md:grid-cols-3 gap-4 ml-8">
              <div className="relative">
                <div className="absolute -left-4 top-2 w-8 h-8 rounded-full bg-gray-700 border border-orange-500/40 flex items-center justify-center text-white font-bold text-sm z-10">2</div>
                <div className="bg-gray-800/60 border border-gray-700 rounded-xl p-5 ml-6 hover:border-orange-500/30 transition">
                  <h4 className="text-sm font-bold mb-1 text-orange-400" style={{ fontFamily: 'Outfit, sans-serif' }}>Autodata</h4>
                  <p className="text-xs text-gray-500 mb-3">Consulta automática a la API oficial</p>
                  <div className="space-y-1.5 text-xs text-gray-400">
                    <div className="flex items-center gap-2"><span className="text-orange-400">✓</span><span>Precio de Toma</span></div>
                    <div className="flex items-center gap-2"><span className="text-orange-400">✓</span><span>Precio de Venta</span></div>
                  </div>
                  <div className="mt-3 text-xs bg-orange-500/10 text-orange-400 px-3 py-1.5 rounded-lg border border-orange-500/20">Inmediato</div>
                </div>
              </div>

              <div className="relative">
                <div className="absolute -left-4 top-2 w-8 h-8 rounded-full bg-gray-700 border border-orange-500/40 flex items-center justify-center text-white font-bold text-sm z-10">3</div>
                <div className="bg-gray-800/60 border border-gray-700 rounded-xl p-5 ml-6 hover:border-orange-500/30 transition">
                  <h4 className="text-sm font-bold mb-1 text-orange-400" style={{ fontFamily: 'Outfit, sans-serif' }}>MercadoLibre</h4>
                  <p className="text-xs text-gray-500 mb-3">Scraping automático de similares</p>
                  <div className="space-y-1.5 text-xs text-gray-400">
                    <div className="flex items-center gap-2"><span className="text-orange-400">✓</span><span>Promedio de mercado</span></div>
                    <div className="flex items-center gap-2"><span className="text-orange-400">✓</span><span>Precio menor/mayor</span></div>
                  </div>
                  <div className="mt-3 text-xs bg-orange-500/10 text-orange-400 px-3 py-1.5 rounded-lg border border-orange-500/20">2–3 minutos</div>
                </div>
              </div>

              <div className="relative">
                <div className="absolute -left-4 top-2 w-8 h-8 rounded-full bg-gray-700 border border-orange-500/40 flex items-center justify-center text-white font-bold text-sm z-10">4</div>
                <div className="bg-gray-800/60 border border-gray-700 rounded-xl p-5 ml-6 hover:border-orange-500/30 transition">
                  <h4 className="text-sm font-bold mb-1 text-orange-400" style={{ fontFamily: 'Outfit, sans-serif' }}>Gerencia — WhatsApp</h4>
                  <p className="text-xs text-gray-500 mb-3">Notificación directa a los 2 dueños</p>
                  <div className="space-y-1.5 text-xs text-gray-400">
                    <div className="flex items-center gap-2"><span className="text-orange-400">✓</span><span>Valoración del vendedor</span></div>
                    <div className="flex items-center gap-2"><span className="text-orange-400">✓</span><span>Expectativa del cliente</span></div>
                  </div>
                  <div className="mt-3 text-xs bg-orange-500/10 text-orange-400 px-3 py-1.5 rounded-lg border border-orange-500/20">Responden c/u</div>
                </div>
              </div>
            </div>

            {/* Paso 5 - Revendedores */}
            <div className="relative ml-8">
              <div className="absolute -left-4 top-2 w-8 h-8 rounded-full bg-gray-700 border border-orange-500/40 flex items-center justify-center text-white font-bold text-sm z-10">5</div>
              <div className="bg-gray-800/60 border border-gray-700 rounded-xl p-5 ml-6 hover:border-orange-500/30 transition">
                <h4 className="text-sm font-bold mb-2 text-orange-400" style={{ fontFamily: 'Outfit, sans-serif' }}>Sistema de Ofertas — Revendedores</h4>
                <p className="text-sm text-gray-400 mb-4">La publicación se distribuye a todos los revendedores registrados. Ellos pueden ver la información y ofertar en tiempo real.</p>
                <div className="grid md:grid-cols-2 gap-3 text-sm text-gray-400">
                  <div className="flex items-start gap-2"><span className="text-orange-400 mt-0.5 flex-shrink-0">→</span><span>Ofertan directamente desde su panel</span></div>
                  <div className="flex items-start gap-2"><span className="text-orange-400 mt-0.5 flex-shrink-0">→</span><span>Ofertas ordenadas mayor a menor en tiempo real</span></div>
                  <div className="flex items-start gap-2"><span className="text-orange-400 mt-0.5 flex-shrink-0">→</span><span>Si alguien supera la mayor oferta, sube al top</span></div>
                  <div className="flex items-start gap-2"><span className="text-orange-400 mt-0.5 flex-shrink-0">→</span><span>Se muestran top 3 + opción de ver todas</span></div>
                </div>
              </div>
            </div>

            {/* Paso 6 - FINAL */}
            <div className="relative">
              <div className="absolute -left-6 top-0 w-12 h-12 rounded-full bg-gray-700 border-2 border-orange-500/60 flex items-center justify-center text-white font-bold text-lg shadow-lg z-10">6</div>
              <div className="bg-gray-900 border border-orange-500/25 rounded-2xl p-8 ml-8 shadow-2xl">
                <h3 className="text-2xl font-bold mb-4 text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Vista Consolidada — Decisión Final
                </h3>
                <p className="mb-6 text-gray-300 leading-relaxed">
                  Toda la información centralizada en una sola pantalla. El sistema unifica datos de todas las fuentes para facilitar la toma de decisiones de gerencia.
                </p>

                <div className="bg-gray-800 rounded-xl p-6 mb-5 border border-gray-700">
                  <h4 className="font-semibold mb-4 text-orange-400 text-sm uppercase tracking-wider">Información Consolidada</h4>
                  <div className="grid sm:grid-cols-3 gap-3 mb-4">
                    <div className="text-center p-3 bg-gray-900 rounded-lg border border-gray-700">
                      <div className="text-xs text-gray-500 mb-1">Autodata Toma</div>
                      <div className="text-xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>$12,500</div>
                    </div>
                    <div className="text-center p-3 bg-gray-900 rounded-lg border border-gray-700">
                      <div className="text-xs text-gray-500 mb-1">Promedio ML</div>
                      <div className="text-xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>$13,200</div>
                    </div>
                    <div className="text-center p-3 bg-gray-900 rounded-lg border-2 border-orange-500/50">
                      <div className="text-xs text-gray-500 mb-1">Mejor Oferta</div>
                      <div className="text-xl font-bold text-orange-400" style={{ fontFamily: 'Outfit, sans-serif' }}>$13,500</div>
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-2 text-sm text-gray-400">
                    <div className="flex items-center gap-2"><span className="text-orange-400">✓</span><span>Expectativa del cliente</span></div>
                    <div className="flex items-center gap-2"><span className="text-orange-400">✓</span><span>Valoración del vendedor</span></div>
                    <div className="flex items-center gap-2"><span className="text-orange-400">✓</span><span>Valoraciones de gerencia (2 dueños)</span></div>
                    <div className="flex items-center gap-2"><span className="text-orange-400">✓</span><span>Todas las ofertas de revendedores</span></div>
                  </div>
                </div>

                <div className="bg-gray-800 rounded-xl p-5 border border-gray-700">
                  <h4 className="font-semibold mb-3 text-white text-sm">Con esta información, gerencia puede:</h4>
                  <div className="grid sm:grid-cols-2 gap-3 text-sm text-gray-300">
                    <div className="flex items-start gap-2"><span className="text-orange-400 mt-0.5 flex-shrink-0">→</span><span><strong className="text-white">Tomar el vehículo</strong> basándose en datos reales</span></div>
                    <div className="flex items-start gap-2"><span className="text-orange-400 mt-0.5 flex-shrink-0">→</span><span><strong className="text-white">Adjudicar a un revendedor</strong> con un click</span></div>
                    <div className="flex items-start gap-2"><span className="text-orange-400 mt-0.5 flex-shrink-0">→</span><span><strong className="text-white">Rechazar la operación</strong> si los números no cierran</span></div>
                    <div className="flex items-start gap-2"><span className="text-orange-400 mt-0.5 flex-shrink-0">→</span><span><strong className="text-white">Comparar múltiples fuentes</strong> antes de decidir</span></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Funcionalidades */}
      <section id="funcionalidades" className="px-6 py-20 border-b border-gray-800">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Funcionalidades del Sistema
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              El sistema consulta y consolida información de diferentes fuentes para ofrecer una visión completa del valor del vehículo
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {funcionalidades.map((func, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 rounded-xl p-6 hover:border-orange-500/50 transition group"
              >
                <div className="w-12 h-12 rounded-lg bg-orange-500/10 flex items-center justify-center text-orange-500 mb-4 group-hover:bg-orange-500/20 transition">
                  {func.icon}
                </div>
                <h3 className="text-lg font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {func.title}
                </h3>
                <p className="text-sm text-gray-400 leading-relaxed">
                  {func.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* El sistema en acción — Device Mockups */}
      <section id="screenshots" className="px-6 py-20 border-b border-gray-800 overflow-hidden">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block px-3 py-1 text-xs font-semibold text-orange-400 border border-orange-500/30 rounded-full mb-4 uppercase tracking-wider">
              Demo real
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              El sistema, en acción
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Panel de gestión profesional. Pensado para trabajar desde cualquier dispositivo.
            </p>
          </div>

          {/* Laptop Mockup — Dashboard */}
          <div className="mb-16">
            <div className="max-w-4xl mx-auto">
              {/* Screen + body */}
              <div
                style={{
                  background: 'linear-gradient(145deg, #2a2a2a 0%, #1a1a1a 100%)',
                  borderRadius: '14px 14px 0 0',
                  padding: '14px 14px 0',
                  boxShadow: '0 4px 0 #111, 0 30px 80px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.08)',
                }}
              >
                {/* Webcam dot */}
                <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#383838', margin: '0 auto 10px', border: '1px solid #444' }} />
                {/* Screen bezel */}
                <div style={{ borderRadius: '6px 6px 0 0', overflow: 'hidden', background: '#000', border: '1px solid #111' }}>
                  <img
                    src="/screenshots/dashboard.png"
                    alt="Dashboard del sistema de tasación"
                    style={{ width: '100%', display: 'block' }}
                  />
                </div>
              </div>
              {/* Laptop base */}
              <div
                style={{
                  background: 'linear-gradient(to bottom, #232323 0%, #1a1a1a 100%)',
                  height: 28,
                  borderRadius: '0 0 6px 6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 8px 20px rgba(0,0,0,0.5)',
                }}
              >
                <div style={{ width: 90, height: 5, background: '#333', borderRadius: 3 }} />
              </div>
              {/* Stand shadow */}
              <div style={{ height: 6, maxWidth: '80%', margin: '0 auto', background: 'rgba(0,0,0,0.4)', borderRadius: '0 0 50% 50%', filter: 'blur(8px)' }} />
            </div>
            <p className="text-center text-xs text-gray-600 mt-4">
              Dashboard con métricas en tiempo real, gráficas animadas y tabla de publicaciones
            </p>
          </div>

          {/* Phone Mockups — Mobile views */}
          <div>
            <h3 className="text-center text-sm font-semibold text-gray-500 uppercase tracking-wider mb-10">
              Optimizado para móvil
            </h3>
            <div className="flex flex-col sm:flex-row items-end justify-center gap-8 sm:gap-12">

              {/* Phone 1 — Form */}
              <div className="flex flex-col items-center gap-3">
                <div
                  style={{
                    background: 'linear-gradient(145deg, #2a2a2a 0%, #1a1a1a 100%)',
                    borderRadius: 38,
                    padding: 10,
                    boxShadow: '0 25px 60px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.08)',
                    position: 'relative',
                    width: 230,
                  }}
                >
                  {/* Notch */}
                  <div style={{
                    position: 'absolute',
                    top: 16,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 70,
                    height: 22,
                    background: '#1a1a1a',
                    borderRadius: 12,
                    zIndex: 2,
                    border: '1px solid #111',
                  }} />
                  {/* Side buttons */}
                  <div style={{ position: 'absolute', left: -3, top: 80, width: 3, height: 28, background: '#2a2a2a', borderRadius: '2px 0 0 2px' }} />
                  <div style={{ position: 'absolute', left: -3, top: 120, width: 3, height: 44, background: '#2a2a2a', borderRadius: '2px 0 0 2px' }} />
                  <div style={{ position: 'absolute', right: -3, top: 100, width: 3, height: 44, background: '#2a2a2a', borderRadius: '0 2px 2px 0' }} />
                  <div style={{ borderRadius: 28, overflow: 'hidden', border: '1px solid #111' }}>
                    <img
                      src="/screenshots/form_mobile.png"
                      alt="Formulario de nueva publicación"
                      style={{ width: '100%', display: 'block' }}
                    />
                  </div>
                  {/* Home bar */}
                  <div style={{ width: 80, height: 4, background: '#333', borderRadius: 3, margin: '8px auto 2px' }} />
                </div>
                <p className="text-xs text-gray-500 text-center max-w-[180px]">Carga rápida de vehículos desde el celular</p>
              </div>

              {/* Phone 2 — Detail (slightly taller / center) */}
              <div className="flex flex-col items-center gap-3" style={{ marginBottom: -20 }}>
                <div
                  style={{
                    background: 'linear-gradient(145deg, #2a2a2a 0%, #1a1a1a 100%)',
                    borderRadius: 38,
                    padding: 10,
                    boxShadow: '0 30px 80px rgba(0,0,0,0.8), 0 0 0 1px rgba(249,115,22,0.15), inset 0 1px 0 rgba(255,255,255,0.08)',
                    position: 'relative',
                    width: 250,
                  }}
                >
                  <div style={{
                    position: 'absolute',
                    top: 16,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 70,
                    height: 22,
                    background: '#1a1a1a',
                    borderRadius: 12,
                    zIndex: 2,
                    border: '1px solid #111',
                  }} />
                  <div style={{ position: 'absolute', left: -3, top: 80, width: 3, height: 28, background: '#2a2a2a', borderRadius: '2px 0 0 2px' }} />
                  <div style={{ position: 'absolute', left: -3, top: 120, width: 3, height: 44, background: '#2a2a2a', borderRadius: '2px 0 0 2px' }} />
                  <div style={{ position: 'absolute', right: -3, top: 100, width: 3, height: 44, background: '#2a2a2a', borderRadius: '0 2px 2px 0' }} />
                  <div style={{ borderRadius: 28, overflow: 'hidden', border: '1px solid #111' }}>
                    <img
                      src="/screenshots/detail_mobile.png"
                      alt="Vista detalle del vehículo"
                      style={{ width: '100%', display: 'block' }}
                    />
                  </div>
                  <div style={{ width: 80, height: 4, background: '#333', borderRadius: 3, margin: '8px auto 2px' }} />
                </div>
                <p className="text-xs text-gray-500 text-center max-w-[200px]">Vista consolidada con todos los datos de tasación</p>
              </div>

            </div>
          </div>

          {/* CTA to try demo */}
          <div className="text-center mt-16">
            <p className="text-sm text-gray-500 mb-4">Estos son los datos reales del sistema. Podés explorarlo ahora.</p>
            <button
              onClick={() => { setPasswordModalOpen(true); }}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-white transition"
              style={{ background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)' }}
            >
              Probar demo
              <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </section>

      <section id="inversion" className="px-6 py-20 bg-gray-900/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Inversión
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Desarrollo a medida con todas las funcionalidades implementadas y listas para producción
            </p>
          </div>

          {/* Desarrollo */}
          <div className="max-w-3xl mx-auto mb-12">
            <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/10 border-2 border-blue-500/50 rounded-2xl p-8 relative overflow-hidden">
              {/* Badge esquina superior derecha — Descuento */}
              <div className="absolute top-0 right-0" data-testid="pack-discount-badge">
                <div className="bg-gradient-to-br from-orange-500 to-orange-600 text-white px-4 py-3 rounded-bl-2xl shadow-lg">
                  <div className="text-[10px] font-semibold uppercase tracking-wider opacity-90 leading-none mb-0.5">Pack integrado</div>
                  <div className="text-2xl font-bold leading-none" style={{ fontFamily: 'Outfit, sans-serif' }}>30% OFF</div>
                </div>
              </div>

              <div className="inline-block px-3 py-1 bg-blue-500 text-white text-xs font-bold rounded-full mb-4">
                INVERSIÓN INICIAL
              </div>

              {/* Precios: antes (tachado) + ahora */}
              <div className="flex flex-wrap items-end gap-x-5 gap-y-2 mb-2">
                <div className="text-5xl font-bold text-white leading-none" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="pack-price-now">
                  USD 4,250
                </div>
                <div className="flex items-baseline gap-2 pb-1">
                  <span className="text-2xl line-through" style={{ color: '#94a3b8', fontFamily: 'Outfit, sans-serif' }} data-testid="pack-price-before">
                    USD 6,070
                  </span>
                  <span className="text-gray-400 text-sm">pago único</span>
                </div>
              </div>

              {/* Línea de ahorro destacada */}
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-orange-500/10 border border-orange-500/30 rounded-full mb-6" data-testid="pack-savings">
                <span className="w-1.5 h-1.5 rounded-full bg-orange-400" />
                <span className="text-sm font-semibold text-orange-300">
                  Ahorrás <span className="text-orange-200">USD 1,820</span> al contratar los 2 módulos juntos
                </span>
              </div>

              <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Desarrollo e Implementación Completa
              </h3>
              <p className="text-xs text-gray-400 mb-5">
                Tasador de Vehículos + Plataforma de Usados B2B · Contratados juntos como pack integrado
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                {pricingOptions[0].features.map((feature, i) => (
                  <div key={i} className="flex items-start gap-3 text-sm text-gray-300">
                    <CheckCircle size={18} className="text-blue-400 flex-shrink-0 mt-0.5" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Mantenimiento Mensual - 2 Opciones */}
          <div>
            <h3 className="text-2xl font-bold text-white text-center mb-8" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Mantenimiento Mensual
            </h3>
            <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
              {monthlyOptions.map((option, index) => (
                <div
                  key={index}
                  data-testid={`pricing-card-${index}`}
                  className={`rounded-2xl p-8 border-2 transition flex flex-col ${
                    option.tag === 'RECOMENDADO'
                      ? 'bg-gradient-to-br from-orange-500/10 to-orange-600/10 border-orange-500/50 shadow-lg shadow-orange-500/10'
                      : 'bg-gray-800/50 border-gray-700'
                  }`}
                >
                  {option.tag && (
                    <div className="inline-block px-3 py-1 text-white text-xs font-bold rounded-full mb-4 w-fit bg-orange-500">
                      {option.tag}
                    </div>
                  )}
                  <div className="mb-2">
                    <div className="text-xs text-gray-500 mb-1">{option.subtitle}</div>
                    <h4 className="text-xl font-bold text-white mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {option.name}
                    </h4>
                  </div>
                  <div className="mb-6">
                    <span className="text-4xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>{option.price}</span>
                    <span className="text-sm text-gray-500 ml-2">/{option.priceNote}</span>
                  </div>
                  <ul className="space-y-2.5 flex-1">
                    {option.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-sm text-gray-300">
                        <CheckCircle size={15} className={`flex-shrink-0 mt-0.5 ${
                          option.tag === 'RECOMENDADO' ? 'text-orange-400' : 'text-gray-400'
                        }`} />
                        <span>{feature}</span>
                      </li>
                    ))}
                    {option.excluded && option.excluded.map((item, i) => (
                      <li key={`ex-${i}`} className="flex items-start gap-2.5 text-sm text-gray-500 pt-2 mt-2 border-t border-gray-700/40">
                        <X size={15} className="flex-shrink-0 mt-0.5 text-gray-600" />
                        <span><span className="font-semibold text-gray-400">No incluye:</span> {item}</span>
                      </li>
                    ))}
                  </ul>
                  {option.note && (
                    <div className="mt-5 pt-4 border-t border-gray-700/60" data-testid={`pricing-note-${index}`}>
                      <p className="text-xs italic text-gray-400 leading-relaxed">
                        <span className="text-orange-400 font-semibold not-italic">Nota:</span> {option.note}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="mt-10 text-center">
            <p className="text-sm text-gray-500 mb-6">
              *Precios en dólares estadounidenses. Demo mockup incluida en la propuesta.
            </p>
            <a
              href="mailto:andres@artificialmente.uy"
              className="inline-flex items-center gap-2 text-orange-500 hover:text-orange-400 transition font-medium"
            >
              Solicitar reunión
              <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-800 px-6 py-12">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <img src={LOGO_HORIZ_WHITE} alt="Artificialmente" className="h-7 w-auto mb-2" />
            <p className="text-xs text-gray-600">
              © 2025 Artificialmente · Propuesta para Vladimir & Aerocar
            </p>
          </div>
          <div className="flex items-center gap-6">
            <button
              onClick={() => navigate('/')}
              className="text-sm text-gray-400 hover:text-white transition"
            >
              Propuesta Original
            </button>
            <a
              href="mailto:andres@artificialmente.uy"
              className="text-sm text-gray-400 hover:text-white transition"
            >
              Contacto
            </a>
          </div>
        </div>
      </footer>

      {/* Password Modal */}
      {passwordModalOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/60 z-40"
            onClick={() => setPasswordModalOpen(false)}
          />
          <div className="fixed inset-0 flex items-center justify-center z-50 px-6">
            <div className="bg-gray-900 border border-gray-700 rounded-2xl p-8 max-w-md w-full">
              <div className="w-16 h-16 rounded-full bg-orange-500/10 flex items-center justify-center mx-auto mb-6">
                <Lock className="text-orange-500" size={28} />
              </div>
              <h2 className="text-2xl font-bold text-white text-center mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Acceso a la Demo
              </h2>
              <p className="text-sm text-gray-400 text-center mb-6">
                Ingresa la contraseña proporcionada para acceder
              </p>
              <form onSubmit={handleSubmitPassword}>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Contraseña"
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 outline-none focus:border-orange-500 transition mb-4"
                  autoFocus
                />
                {error && (
                  <div className="bg-red-500/10 border border-red-500/20 text-red-400 text-sm px-4 py-3 rounded-lg mb-4">
                    Contraseña incorrecta
                  </div>
                )}
                <button
                  type="submit"
                  className="w-full px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white font-semibold rounded-lg hover:from-orange-600 hover:to-orange-700 transition"
                >
                  Acceder
                </button>
              </form>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
